// components/TemplateVarsDialog.js
//
// Pede os valores que faltam antes de disparar um template com variáveis.
//
// Out/26. Só abre quando falta algo: se o mapeamento do template resolve
// tudo a partir do contato (nome, campo do CRM, texto fixo), o envio sai
// direto e este diálogo nunca aparece. Perguntar o que já se sabe seria
// atrito puro no dia a dia de quem manda dezenas de mensagens.
//
// Quem decide o que falta é o servidor, no /templates/preflight — a mesma
// resolução que o envio faz. Duas implementações do "o que falta?" acabariam
// discordando, e a discordância apareceria como recusa da Meta depois do
// disparo.

import { useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Stack, TextField, Typography,
} from '@mui/material';
import { FiSend } from 'react-icons/fi';

const COMPONENT_LABELS = {
  HEADER: 'cabeçalho',
  BODY: 'mensagem',
  BUTTON_URL: 'link do botão',
};

export default function TemplateVarsDialog({
  open, onClose, templateName, contactName, missing = [], preview = '', sending = false, onConfirm,
}) {
  const [values, setValues] = useState({});

  const faltamPreencher = missing.filter((m) => !(values[m.slot] || '').trim());
  const podeEnviar = faltamPreencher.length === 0 && !sending;

  const handleConfirm = () => {
    if (!podeEnviar) return;
    const limpos = Object.fromEntries(
      Object.entries(values).map(([k, v]) => [k, (v || '').trim()])
    );
    onConfirm?.(limpos);
  };

  return (
    <Dialog
      open={open}
      onClose={sending ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 800, fontSize: '1.05rem', pb: 0.5 }}>
        Preencher antes de enviar
        <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', fontWeight: 500 }}>
          {templateName}{contactName ? ` · para ${contactName}` : ''}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {preview && (
          <Box sx={{
            bgcolor: '#d9fdd3', borderRadius: 2, p: 1.5, mb: 2.5,
            fontSize: '0.86rem', color: '#111b21', whiteSpace: 'pre-wrap',
          }}>
            {preview}
          </Box>
        )}

        <Alert severity="info" sx={{ mb: 2, borderRadius: 2, fontSize: '0.8rem' }}>
          {missing.length === 1
            ? 'Um valor não veio do cadastro do contato.'
            : `${missing.length} valores não vieram do cadastro do contato.`}
          {' '}Preencha para continuar — a Meta recusa mensagem com variável em branco.
        </Alert>

        <Stack spacing={2}>
          {missing.map((m) => (
            <Box key={m.slot}>
              <Stack direction="row" alignItems="center" spacing={1} mb={0.75}>
                <Chip
                  label={`{{${m.position}}}`}
                  size="small"
                  sx={{ fontFamily: 'monospace', fontWeight: 700, bgcolor: '#fef3c7', color: '#92400e' }}
                />
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  no {COMPONENT_LABELS[m.component] || m.component}
                </Typography>
              </Stack>
              <TextField
                fullWidth
                size="small"
                autoFocus={m === missing[0]}
                value={values[m.slot] || ''}
                onChange={(e) => setValues((prev) => ({ ...prev, [m.slot]: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter' && podeEnviar) handleConfirm(); }}
                disabled={sending}
                inputProps={{ maxLength: 300 }}
                // O motivo vem do servidor e nomeia o campo que faltou
                // ("O contato não tem Valor do boleto preenchido"), em vez
                // de um genérico "campo obrigatório".
                helperText={m.reason}
              />
            </Box>
          ))}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={sending} sx={{ textTransform: 'none', color: '#64748b' }}>
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={!podeEnviar}
          variant="contained"
          disableElevation
          startIcon={sending ? <CircularProgress size={13} color="inherit" /> : <FiSend size={14} />}
          sx={{
            textTransform: 'none', fontWeight: 700, bgcolor: '#25d366', color: '#0f172a',
            '&:hover': { bgcolor: '#16a34a', color: '#fff' },
            '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' },
          }}
        >
          {sending ? 'Enviando…' : 'Enviar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
